import { logAirangeBuildInfo } from "@airange/shared/browser-build-info";
import { buildDocumentTitle } from "@airange/shared/index-airange-admin";
import { LOGIN_PATH, OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import {
  buildPlatformShellAvatarProps,
  PlatformShellActions,
  registerChunkLoadErrorReloadOnce,
} from "@airange/ui";
import {
  AppstoreOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import type { Settings as LayoutSettings, MenuDataItem } from "@ant-design/pro-components";
import type { RuntimeConfig } from "@umijs/max";
import { getIntl, history, Link } from "@umijs/max";
import type { ConfigProviderProps, MenuProps } from "antd";
import { Alert, App as AntdApp, Button, ConfigProvider, Menu } from "antd";
import type React from "react";
import "antd/dist/reset.css";
import defaultSettings from "@/app/default-settings";
import { LocaleSwitch } from "./components/LocaleSwitch";
import {
  buildAdminLoginPath,
  canonicalizeAdminBrowserLocation,
  isAdminAuthBrowserPath,
  resolveAdminInternalPathname,
} from "./lib/admin-auth-routes";
import { bootstrapAdminBffSession, logout } from "./lib/admin-auth-session";
import { resolveAdminSsoGuardRedirect } from "./lib/admin-sso-guard";
import { PORTAL_BASE_URL } from "./lib/env";
import { getPortalSettings } from "./lib/identity-portal-api";
import { initializePlatformAdminLocale, loadPlatformAdminLocale } from "./lib/locale";
import { resolveCenterTitleId, resolvePageTitleId } from "./lib/page-title-rules";
import { getPlatformShellConfig } from "./lib/platform-shell-config";
import { toAppLocalUrlPath } from "./lib/public-path";
import {
  clearSession,
  clearStoredAuthSource,
  markRecentSsoLogout,
  restoreImpersonationActorSession,
} from "./lib/session";
import { canManagePlatform, resolveAdminDefaultPath } from "./types/auth";
import "./styles.css";

canonicalizeAdminBrowserLocation();
logAirangeBuildInfo(__AIRANGE_BUILD_INFO__);

type LayoutMenuItem = MenuDataItem & {
  children?: LayoutMenuItem[];
  disabledTooltip?: boolean;
};

type InitialState = {
  settings?: Partial<LayoutSettings> | null;
  currentUser?: Awaited<ReturnType<typeof getInitialState>>["currentUser"];
  publicConfig?: Awaited<ReturnType<typeof getInitialState>>["publicConfig"];
  antdLocale?: NonNullable<ConfigProviderProps["locale"]>;
};

type PageTitleRenderProps = {
  pathname?: string;
  title?: string | false;
  pageName?: string;
  location?: {
    pathname?: string;
  };
};

type PlatformDomainMenuProps = {
  isMobile?: boolean;
  location?: {
    pathname?: string;
  };
  menuData?: LayoutMenuItem[];
};

type PlatformMenuDomain = {
  key: string;
  icon: React.ReactNode;
  titleId: string;
  centerPaths: readonly string[];
};

const PLATFORM_MENU_DOMAINS: readonly PlatformMenuDomain[] = [
  {
    key: "overview",
    icon: <DashboardOutlined />,
    titleId: "platform.admin.menu.domainOverview",
    centerPaths: ["/dashboard", "/centers"],
  },
  {
    key: "portal",
    icon: <AppstoreOutlined />,
    titleId: "platform.admin.menu.domainPortal",
    centerPaths: ["/portal", "/applications"],
  },
  {
    key: "governance",
    icon: <SafetyCertificateOutlined />,
    titleId: "platform.admin.menu.domainGovernance",
    centerPaths: ["/identity", "/assets-center", "/business-reference-center"],
  },
  {
    key: "resource",
    icon: <DatabaseOutlined />,
    titleId: "platform.admin.menu.domainResource",
    centerPaths: ["/environment-center", "/instance-center"],
  },
  {
    key: "runtime",
    icon: <DeploymentUnitOutlined />,
    titleId: "platform.admin.menu.domainRuntime",
    centerPaths: ["/orchestrator-center", "/execution-center", "/task-center", "/ops-center"],
  },
  {
    key: "capability",
    icon: <ToolOutlined />,
    titleId: "platform.admin.menu.domainCapability",
    centerPaths: [
      "/intelligence-center",
      "/tools-library",
      "/traffic-library",
      "/knowledge-library",
      "/capability-graph",
      "/tactics-techniques-hub",
      "/vulnerability-library",
    ],
  },
];

function disableMenuItemTooltips(
  items: LayoutMenuItem[] | undefined,
): LayoutMenuItem[] | undefined {
  if (!items) return items;
  return items.map((item) => ({
    ...item,
    disabledTooltip: true,
    children: disableMenuItemTooltips(item.children),
  }));
}

function normalizeMenuPath(path: unknown): string {
  const rawPath = String(path || "").trim();
  if (!rawPath) return "";
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  const normalizedPath = `/${rawPath}`.replace(/\/+/g, "/");
  return normalizedPath.length > 1 ? normalizedPath.replace(/\/+$/, "") : normalizedPath;
}

function isPathActive(pathname: string, centerPath: string): boolean {
  if (!centerPath) return false;
  if (centerPath === "/") return pathname === "/";
  return pathname === centerPath || pathname.startsWith(`${centerPath}/`);
}

function readMenuItemLabel(item: LayoutMenuItem): React.ReactNode {
  const name = item.name;
  if (typeof name === "string" && name.trim()) {
    return name;
  }
  return normalizeMenuPath(item.path) || String(item.key || "");
}

function renderPlatformDomainLabel(icon: React.ReactNode, label: string): React.ReactNode {
  return (
    <span className="platform-domain-menu-label">
      <span className="platform-domain-menu-label__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="platform-domain-menu-label__text">{label}</span>
    </span>
  );
}

function renderPlatformDomainTopNav(
  props: PlatformDomainMenuProps,
  defaultDom: React.ReactNode,
  intl: ReturnType<typeof getIntl>,
): React.ReactNode {
  if (props.isMobile) {
    return defaultDom;
  }

  const menuByPath = new Map<string, LayoutMenuItem>();
  (props.menuData || []).forEach((item) => {
    const itemPath = normalizeMenuPath(item.path);
    if (itemPath) {
      menuByPath.set(itemPath, item);
    }
  });

  const routeByMenuKey = new Map<string, string>();
  const items: MenuProps["items"] = PLATFORM_MENU_DOMAINS.map((domain) => {
    const children = domain.centerPaths
      .map((centerPath) => {
        const item = menuByPath.get(centerPath);
        if (!item) return null;
        const normalizedPath = normalizeMenuPath(item.path);
        const key = `${domain.key}:${normalizedPath}`;
        routeByMenuKey.set(key, normalizedPath);
        return {
          key,
          icon: item.icon,
          label: (
            <Link prefetch="intent" to={normalizedPath}>
              {readMenuItemLabel(item)}
            </Link>
          ),
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    if (!children.length) {
      return null;
    }
    return {
      key: domain.key,
      label: renderPlatformDomainLabel(domain.icon, intl.formatMessage({ id: domain.titleId })),
      children,
    };
  }).filter((item): item is NonNullable<typeof item> => Boolean(item));

  if (!items.length) {
    return defaultDom;
  }

  const pathname = normalizeMenuPath(props.location?.pathname || readCurrentPathname()) || "/";
  const activeDomain = PLATFORM_MENU_DOMAINS.find((domain) =>
    domain.centerPaths.some((centerPath) => isPathActive(pathname, centerPath)),
  );

  return (
    <Menu
      mode="horizontal"
      selectedKeys={activeDomain ? [activeDomain.key] : []}
      items={items}
      onClick={({ key, domEvent }) => {
        if (domEvent?.defaultPrevented) return;
        const targetPath = routeByMenuKey.get(String(key));
        if (targetPath && targetPath !== pathname) {
          history.push(targetPath);
        }
      }}
      style={{
        width: "100%",
        minWidth: 0,
        borderBottom: "none",
        background: "transparent",
      }}
    />
  );
}

export async function getInitialState() {
  const locale = initializePlatformAdminLocale();
  const { antdLocale } = await loadPlatformAdminLocale(locale);
  const settings = defaultSettings as Partial<LayoutSettings>;
  const publicConfig = getPlatformShellConfig();
  if (isAdminAuthBrowserPath(readCurrentPathname(), OIDC_CALLBACK_PATH)) {
    return {
      settings,
      currentUser: null,
      publicConfig,
      antdLocale,
    };
  }

  try {
    const nextUser = await bootstrapAdminBffSession();
    if (!nextUser || !canManagePlatform(nextUser)) {
      clearSession();
      clearStoredAuthSource();
      return {
        settings,
        currentUser: null,
        publicConfig,
        antdLocale,
      };
    }
    let mergedPublicConfig = publicConfig;
    try {
      const portalSettings = await getPortalSettings();
      mergedPublicConfig = {
        ...publicConfig,
        platform_name:
          String(portalSettings.branding.portal_name || "").trim() || publicConfig.platform_name,
        logo_url: portalSettings.branding.logo_url || publicConfig.logo_url,
      };
    } catch {
      mergedPublicConfig = publicConfig;
    }
    return {
      settings,
      currentUser: nextUser,
      publicConfig: mergedPublicConfig,
      antdLocale,
    };
  } catch {
    clearSession();
    clearStoredAuthSource();
    return {
      settings,
      currentUser: null,
      publicConfig,
      antdLocale,
    };
  }
}

function readCurrentPathname(): string {
  if (typeof window !== "undefined") {
    return window.location.pathname || "/";
  }
  return "/";
}

function readCurrentSearch(): string {
  if (typeof window !== "undefined") {
    return window.location.search || "";
  }
  return "";
}

function readCurrentHash(): string {
  if (typeof window !== "undefined") {
    return window.location.hash || "";
  }
  return "";
}

export const layout: RuntimeConfig["layout"] = ({
  initialState,
}: {
  initialState?: InitialState;
}) => {
  const currentUser = initialState?.currentUser ?? null;
  const intl = getIntl();
  const publicConfig = initialState?.publicConfig;
  const siteTitle =
    publicConfig?.platform_name || intl.formatMessage({ id: "platform.admin.title" });

  return {
    ...initialState?.settings,
    title: siteTitle,
    logo: publicConfig?.logo_url || undefined,
    menu: {
      locale: true,
      defaultOpenAll: true,
      ignoreFlatMenu: true,
    },
    pageTitleRender: (
      props: PageTitleRenderProps,
      defaultPageTitle?: string,
      info?: { pageName?: string },
    ) => {
      const rawPathname = String(
        props?.location?.pathname || props?.pathname || readCurrentPathname() || "",
      );
      const isLoginPage = isAdminAuthBrowserPath(rawPathname, LOGIN_PATH);
      if (isLoginPage || isAdminAuthBrowserPath(rawPathname, OIDC_CALLBACK_PATH)) {
        const loginTitle = isLoginPage
          ? intl.formatMessage(
              { id: "platform.admin.login.pageTitle" },
              { platformName: siteTitle },
            )
          : "";
        return buildDocumentTitle({
          kind: isLoginPage ? "login" : "callback",
          siteTitle,
          pageTitle: loginTitle,
        });
      }
      const pathname = toAppLocalUrlPath(rawPathname);
      const resolvedPageTitleId = resolvePageTitleId(pathname);
      const resolvedPageTitle = resolvedPageTitleId
        ? intl.formatMessage({ id: resolvedPageTitleId })
        : null;
      const pageNameCandidates = [resolvedPageTitle, info?.pageName, defaultPageTitle, props?.title]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .filter((item) => item !== siteTitle);
      const pageName = pageNameCandidates[0] || "";
      const centerTitleId = resolveCenterTitleId(pathname);
      const centerName = centerTitleId ? intl.formatMessage({ id: centerTitleId }) : null;
      return buildDocumentTitle({
        siteTitle,
        centerTitle: centerName,
        pageTitle: pageName,
      });
    },
    postMenuData: (menuData: LayoutMenuItem[] | undefined) => {
      return disableMenuItemTooltips(menuData) ?? [];
    },
    headerContentRender: (props, defaultDom) => renderPlatformDomainTopNav(props, defaultDom, intl),
    onPageChange: () => {
      const browserPathname = readCurrentPathname();
      const pathname = resolveAdminInternalPathname(browserPathname);
      if (pathname === OIDC_CALLBACK_PATH) {
        return;
      }
      const defaultAuthorizedPath = resolveAdminDefaultPath(currentUser);
      const redirectTarget = resolveAdminSsoGuardRedirect({
        initialStateReady: Boolean(initialState),
        pathname: browserPathname,
        search: readCurrentSearch(),
        hash: readCurrentHash(),
        hasAccess: Boolean(currentUser),
        defaultAuthorizedPath,
      });
      if (redirectTarget) {
        history.replace(redirectTarget);
        return;
      }
      if (pathname === "/") {
        history.replace(defaultAuthorizedPath);
      }
    },
    actionsRender: () => [
      ...PlatformShellActions({
        currentApp: "airange-admin",
        localeNode: <LocaleSwitch />,
        portalBaseUrl: PORTAL_BASE_URL,
      }),
    ],
    avatarProps: buildPlatformShellAvatarProps({
      currentUser,
      onLogout: async () => {
        try {
          await logout();
        } finally {
          clearSession();
          clearStoredAuthSource();
          markRecentSsoLogout();
          window.location.assign(buildAdminLoginPath("logged_out=1"));
        }
      },
    }),
    childrenRender: (children: React.ReactNode) => {
      const impersonation = currentUser?.impersonation;
      return (
        <ConfigProvider
          locale={initialState?.antdLocale}
          theme={{ cssVar: { key: "airange-admin" } }}
        >
          <AntdApp>
            {impersonation ? (
              <Alert
                banner
                type="warning"
                showIcon={false}
                title={intl.formatMessage(
                  { id: "platform.admin.identity.messages.impersonationBanner" },
                  {
                    actor: impersonation.actor_full_name,
                    actorUsername: impersonation.actor_username,
                    target: currentUser?.full_name || currentUser?.username || "-",
                    targetUsername: currentUser?.username || "-",
                  },
                )}
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      if (!restoreImpersonationActorSession()) {
                        clearSession();
                        clearStoredAuthSource();
                        window.location.assign(buildAdminLoginPath());
                        return;
                      }
                      history.replace(resolveAdminDefaultPath(currentUser));
                    }}
                  >
                    {intl.formatMessage({ id: "common.actions.exitImpersonation" })}
                  </Button>
                }
              />
            ) : null}
            {children}
          </AntdApp>
        </ConfigProvider>
      );
    },
  };
};

// Recover once from stale index/chunk mismatch after deploy.
registerChunkLoadErrorReloadOnce("airange-admin");
